/* ==========================================================================
   OpenClaw AI Chat Bot Widget Component (Persistent Across SPA Navigation)
   Preserves session messages, open/closed modal state, background WebSocket
   streaming and status polling even when the visitor navigates across pages.
   ========================================================================== */

import { getLocale, getCurrentLanguage } from '../locales/i18n.js';

const chatTranslations = {
  en: {
    title: 'AI Support Assistant',
    subtitle: 'Connected to SED Knowledge Base',
    welcome: 'Hello! I am your AI Support Assistant pre-trained on JALUD Sound Event Detector manuals and system architecture. How can I help you?',
    placeholder: 'Ask me about SED...',
    bubblePrompt: 'Ask me anything about SED here',
    bubbleHeader: 'SED Support AI',
    searching: 'Searching the SED knowledge base, please be patient, the answer might take up to 1min',
    agentProcessing: '⏳ OpenClaw support agent is processing your request. Please wait a moment...',
    sources: 'Sources'
  },
  cs: {
    title: 'AI Podpora & Asistent',
    subtitle: 'Propojeno se znalostní bází SED',
    welcome: 'Dobrý den! Jsem váš AI asistent vyškolený na manuálech Sound Event Detector a systémové architektuře JALUD. Jak vám mohu pomoci?',
    placeholder: 'Zeptejte se na cokoliv o SED...',
    bubblePrompt: 'Zeptejte se mě na cokoliv o SED',
    bubbleHeader: 'SED Podpora AI',
    searching: 'Vyhledávám ve znalostní bázi SED, vyčkejte prosím, odpověď může trvat až 1 minutu',
    agentProcessing: '⏳ AI asistent OpenClaw zpracovává váš dotaz. Chvilku strpení...',
    sources: 'Zdroje'
  },
  de: {
    title: 'KI-Support-Assistent',
    subtitle: 'Verbunden mit der SED-Wissensdatenbank',
    welcome: 'Hallo! Ich bin Ihr KI-Support-Assistent, geschult auf den Handbüchern und der Systemarchitektur des JALUD Sound Event Detectors. Wie kann ich Ihnen helfen?',
    placeholder: 'Fragen Sie mich nach SED...',
    bubblePrompt: 'Fragen Sie mich hier alles über SED',
    bubbleHeader: 'SED Support KI',
    searching: 'Durchsuche die SED-Wissensdatenbank, bitte haben Sie einen Moment Geduld...',
    agentProcessing: '⏳ Der OpenClaw-Support-Agent bearbeitet Ihre Anfrage. Bitte warten...',
    sources: 'Quellen'
  },
  es: {
    title: 'Asistente de Soporte IA',
    subtitle: 'Conectado a la base de conocimiento de SED',
    welcome: '¡Hola! Soy su asistente de soporte con IA, entrenado con los manuales y la arquitectura del sistema Sound Event Detector de JALUD. ¿Cómo puedo ayudarle?',
    placeholder: 'Pregúnteme sobre SED...',
    bubblePrompt: 'Pregúnteme cualquier duda sobre SED aquí',
    bubbleHeader: 'SED Soporte IA',
    searching: 'Buscando en la base de conocimientos de SED, por favor espere un momento...',
    agentProcessing: '⏳ El agente de soporte OpenClaw está procesando su solicitud. Espere un momento...',
    sources: 'Fuentes'
  },
  pt: {
    title: 'Assistente de Suporte IA',
    subtitle: 'Conectado à base de conhecimento SED',
    welcome: 'Olá! Sou o seu assistente de suporte por IA, treinado com os manuais e a arquitetura do sistema Sound Event Detector da JALUD. Como posso ajudar?',
    placeholder: 'Pergunte-me sobre o SED...',
    bubblePrompt: 'Tire suas dúvidas sobre o SED aqui',
    bubbleHeader: 'SED Suporte IA',
    searching: 'Pesquisando na base de conhecimento SED, aguarde um momento...',
    agentProcessing: '⏳ O agente de suporte OpenClaw está processando sua solicitação. Aguarde...',
    sources: 'Fontes'
  },
  ar: {
    title: 'مساعد الدعم بالذكاء الاصطناعي',
    subtitle: 'متصل بقاعدة معرفة كاشف الأصوات SED',
    welcome: 'مرحباً! أنا مساعد الدعم بالذكاء الاصطناعي المدرب على أدلة الاستخدام وبنية نظام Sound Event Detector من JALUD. كيف يمكنني مساعدتك اليوم؟',
    placeholder: 'اسألني أي شيء عن نظام SED...',
    bubblePrompt: 'اسألني أي سؤال عن نظام SED هنا',
    bubbleHeader: 'دعم SED الذكي',
    searching: 'جارٍ البحث في قاعدة معرفة SED، يرجى الانتظار للحظات...',
    agentProcessing: '⏳ يقوم وكيل دعم OpenClaw بمعالجة طلبك، يرجى الانتظار...',
    sources: 'المصادر'
  }
};

/**
 * Message history storage helpers
 */
function loadStoredMessages() {
  try {
    const raw = sessionStorage.getItem('sed_openclaw_messages');
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    return [];
  }
}

function saveStoredMessages(msgs) {
  try {
    sessionStorage.setItem('sed_openclaw_messages', JSON.stringify(msgs));
  } catch (e) {}
}

let activeStreamManager = null;

export function renderOpenClawChatWidget() {
  const existing = document.getElementById('sed-openclaw-widget-root');
  if (existing) {
    // If already mounted in document.body, do not destroy or rebuild!
    updateWidgetLanguage();
    return existing;
  }

  // Get or initialize persistent session ID
  let sessionId = localStorage.getItem('sed_openclaw_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
    localStorage.setItem('sed_openclaw_session_id', sessionId);
  }

  const lang = getLocale() || 'en';
  const c = chatTranslations[lang] || chatTranslations.en;

  const widgetRoot = document.createElement('div');
  widgetRoot.id = 'sed-openclaw-widget-root';

  const isModalOpen = sessionStorage.getItem('sed_openclaw_is_open') === '1';

  widgetRoot.innerHTML = `
    <!-- Floating Trigger Button -->
    <button class="sed-chat-trigger" id="sed-chat-open-btn" aria-label="${c.title}" style="display: flex; align-items: center; justify-content: center; padding: 0;">
      <img src="/sed_logo.svg" alt="SED Logo" style="width: 38px; height: 38px; border-radius: 8px; object-fit: contain;" />
    </button>

    <!-- Chat Modal Window -->
    <div class="sed-chat-modal ${isModalOpen ? '' : 'hidden'}" id="sed-chat-modal">
      <div class="sed-chat-header">
        <div class="sed-chat-bot-info">
          <div class="sed-chat-bot-avatar" style="display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,0.15); border-radius: 8px; width: 36px; height: 36px; padding: 2px;">
            <img src="/sed_logo.svg" alt="SED AI" style="width: 30px; height: 30px; object-fit: contain;" />
          </div>
          <div>
            <div id="sed-chat-header-title" style="font-weight: 800; font-size: 0.95rem;">${c.title}</div>
            <div id="sed-chat-header-sub" style="font-size: 0.75rem; opacity: 0.85;">${c.subtitle}</div>
          </div>
        </div>
        <button id="sed-chat-close-btn" style="background: none; border: none; color: #ffffff; font-size: 1.2rem; cursor: pointer;" title="Minimize Chat">✕</button>
      </div>

      <div class="sed-chat-messages" id="sed-chat-msgs-container">
        <!-- Dynamically injected persistent messages -->
      </div>

      <form class="sed-chat-input-area" id="sed-chat-form">
        <input type="text" class="sed-chat-input" id="sed-chat-input-field" placeholder="${c.placeholder}" required autocomplete="off" />
        <button type="submit" class="sed-chat-send" title="Send Message">➔</button>
      </form>
    </div>

    <!-- Interactive Callout Bubble Popup -->
    <div class="sed-chat-bubble-popup ${isModalOpen ? 'hidden' : ''}" id="sed-chat-bubble-popup">
      <div class="sed-chat-bubble-content">
        <div class="sed-chat-bubble-icon">👋</div>
        <div class="sed-chat-bubble-text">
          <strong id="sed-chat-bubble-title" style="display: block; font-size: 0.72rem; color: #35a8df; font-weight: 800; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px;">${c.bubbleHeader}</strong>
          <span id="sed-chat-bubble-span">${c.bubblePrompt}</span>
        </div>
        <button class="sed-chat-bubble-close" id="sed-chat-bubble-close-btn" title="Dismiss">✕</button>
      </div>
      <div class="sed-chat-bubble-arrow"></div>
    </div>
  `;

  document.body.appendChild(widgetRoot);

  const openBtn = document.getElementById('sed-chat-open-btn');
  const closeBtn = document.getElementById('sed-chat-close-btn');
  const modal = document.getElementById('sed-chat-modal');
  const form = document.getElementById('sed-chat-form');
  const input = document.getElementById('sed-chat-input-field');
  const msgsContainer = document.getElementById('sed-chat-msgs-container');
  const bubble = document.getElementById('sed-chat-bubble-popup');

  let bubbleDismissed = sessionStorage.getItem('sed_chat_bubble_dismissed') === '1';

  const showBubble = () => {
    if (!bubbleDismissed && modal && modal.classList.contains('hidden') && bubble) {
      bubble.classList.remove('hidden');
    }
  };

  const hideBubble = () => {
    if (bubble) bubble.classList.add('hidden');
  };

  if (!isModalOpen) {
    setTimeout(showBubble, 800);
    window.addEventListener('scroll', () => {
      if (!bubbleDismissed && modal && modal.classList.contains('hidden')) {
        showBubble();
      }
    }, { passive: true });
  }

  /**
   * Robust Markdown & Format Parser for Chat Responses
   */
  const formatMarkdown = (rawText, sources = []) => {
    if (!rawText) return 'Request processed by OpenClaw Bot.';

    let text = rawText
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');

    text = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    text = text.replace(/(^|\s)\*(.*?)\*(\s|$)/g, '$1<em>$2</em>$3');
    text = text.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #35a8df; text-decoration: underline;">$1</a>');
    text = text.replace(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g, '<a href="mailto:$1" style="color: #35a8df; text-decoration: underline;">$1</a>');

    const lines = text.split('\n');
    const formattedLines = [];
    let inList = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const bulletMatch = line.match(/^(\s*)[-*•]\s+(.*)$/);
      if (bulletMatch) {
        if (!inList) {
          formattedLines.push('<ul style="margin: 6px 0; padding-left: 18px; list-style-type: disc;">');
          inList = true;
        }
        formattedLines.push(`<li style="margin-bottom: 3px;">${bulletMatch[2]}</li>`);
      } else {
        if (inList) {
          formattedLines.push('</ul>');
          inList = false;
        }
        formattedLines.push(line);
      }
    }
    if (inList) formattedLines.push('</ul>');

    let resultHtml = formattedLines.join('<br>').replace(/(<br>\s*){3,}/g, '<br><br>');

    if (sources && sources.length > 0) {
      resultHtml += `<div style="margin-top: 10px; font-size: 0.72rem; opacity: 0.85; border-top: 1px dashed rgba(255,255,255,0.25); padding-top: 6px;">📚 <strong>${c.sources}:</strong> ${sources.join(', ')}</div>`;
    }

    return resultHtml;
  };

  /**
   * Renders all messages from sessionStorage or default welcome
   */
  const renderAllMessages = () => {
    if (!msgsContainer) return;
    msgsContainer.innerHTML = '';
    const stored = loadStoredMessages();

    if (stored.length === 0) {
      const welcomeEl = document.createElement('div');
      welcomeEl.className = 'sed-chat-msg bot';
      welcomeEl.id = 'sed-welcome-msg';
      welcomeEl.innerHTML = c.welcome;
      msgsContainer.appendChild(welcomeEl);
    } else {
      stored.forEach(item => {
        if (item.role === 'user') {
          const uEl = document.createElement('div');
          uEl.className = 'sed-chat-msg user';
          uEl.textContent = item.text;
          msgsContainer.appendChild(uEl);
        } else if (item.role === 'bot') {
          const bEl = document.createElement('div');
          bEl.className = 'sed-chat-msg bot' + (item.isThinking ? ' thinking' : (item.isStreaming ? ' streaming' : ''));
          if (item.isThinking) {
            bEl.innerHTML = `<span style="display: inline-flex; gap: 4px; align-items: center;"><span style="font-size: 0.8rem; opacity: 0.8;">${c.searching}</span> <span style="animation: pulse 1s infinite;">.</span><span style="animation: pulse 1s infinite 0.2s;">.</span><span style="animation: pulse 1s infinite 0.4s;">.</span></span>`;
          } else if (item.isStreaming) {
            bEl.innerHTML = formatMarkdown(item.text) + '<span class="sed-streaming-cursor">▍</span>';
          } else {
            bEl.innerHTML = formatMarkdown(item.text, item.sources || []);
          }
          msgsContainer.appendChild(bEl);
        }
      });
    }
    msgsContainer.scrollTop = msgsContainer.scrollHeight;
  };

  // Initial render of stored messages
  renderAllMessages();

  let isWaitingForReply = false;
  let currentActiveAbortCtrl = null;

  // Check if we have an in-progress pending query from previous page
  const stored = loadStoredMessages();
  if (stored.length > 0 && stored[stored.length - 1].isThinking) {
    isWaitingForReply = true;
  }

  /**
   * Delivers finished reply into thinking/streaming DOM element and saves to storage
   */
  const deliverReply = (replyText, sources = []) => {
    const msgs = loadStoredMessages();
    const last = msgs[msgs.length - 1];
    if (last && last.role === 'bot') {
      last.text = replyText;
      last.sources = sources;
      last.isThinking = false;
      last.isStreaming = false;
      saveStoredMessages(msgs);
    } else {
      msgs.push({
        role: 'bot',
        text: replyText,
        sources,
        isThinking: false,
        isStreaming: false,
        time: Date.now()
      });
      saveStoredMessages(msgs);
    }

    renderAllMessages();

    isWaitingForReply = false;
    if (currentActiveAbortCtrl) {
      try { currentActiveAbortCtrl.abort(); } catch (e) {}
      currentActiveAbortCtrl = null;
    }
  };

  /**
   * WebSocket Streaming Manager for Instant Token Delivery (<300-500ms)
   */
  class ChatStreamManager {
    constructor(sId) {
      this.sessionId = sId;
      this.lastIndex = -1;
      this.accumulatedText = '';
      this.ws = null;
      this.reconnectTimer = null;
      this.isExplicitlyClosed = false;
      this.connect();
    }

    connect(replayFrom = -1) {
      if (this.isExplicitlyClosed) return;
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      try {
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.host || 'jalud.eu';
        const url = `${protocol}//${host}/ws/chat/${encodeURIComponent(this.sessionId)}` +
                    (replayFrom >= 0 ? `?replay_from=${replayFrom}` : '');

        this.ws = new WebSocket(url);

        this.ws.onopen = () => {
          console.log('[OpenClaw WS] 🔌 Connected to persistent chat bridge');
        };

        this.ws.onmessage = (e) => {
          try {
            const msg = JSON.parse(e.data);
            this.handleMessage(msg);
          } catch (err) {
            console.warn('[OpenClaw WS] Message parse error:', err);
          }
        };

        this.ws.onclose = () => {
          if (!this.isExplicitlyClosed) {
            this.reconnectTimer = setTimeout(() => {
              this.connect(this.lastIndex >= 0 ? this.lastIndex : -1);
            }, 1500);
          }
        };

        this.ws.onerror = (err) => {
          console.warn('[OpenClaw WS] WebSocket error:', err);
        };
      } catch (e) {
        console.warn('[OpenClaw WS] Failed to initialize WebSocket:', e.message);
      }
    }

    handleMessage(msg) {
      switch (msg.type) {
        case 'connected':
          break;

        case 'message_queued':
        case 'stream_start': {
          const activeEl = msgsContainer.querySelector('.sed-chat-msg.bot.thinking') || 
                           msgsContainer.querySelector('.sed-chat-msg.bot.streaming');
          if (activeEl) {
            activeEl.classList.remove('thinking');
            activeEl.classList.add('streaming');
          }
          break;
        }

        case 'chunk': {
          const chunkIdx = Number(msg.index !== undefined ? msg.index : 0);
          if (chunkIdx > this.lastIndex || this.lastIndex === -1) {
            this.lastIndex = chunkIdx;
            this.accumulatedText += (msg.text || '');

            // Update DOM and memory
            const activeEl = msgsContainer.querySelector('.sed-chat-msg.bot.streaming') || 
                             msgsContainer.querySelector('.sed-chat-msg.bot.thinking');
            if (activeEl) {
              activeEl.classList.remove('thinking');
              activeEl.classList.add('streaming');
              activeEl.innerHTML = formatMarkdown(this.accumulatedText) + '<span class="sed-streaming-cursor">▍</span>';
              msgsContainer.scrollTop = msgsContainer.scrollHeight;
            }
          }
          break;
        }

        case 'replay_done':
          if (msg.last_index !== undefined && msg.last_index > this.lastIndex) {
            this.lastIndex = msg.last_index;
          }
          break;

        case 'done':
          this.accumulatedText = msg.full_text || this.accumulatedText;
          deliverReply(this.accumulatedText, msg.sources || []);
          this.accumulatedText = '';
          this.lastIndex = -1;
          break;

        case 'error':
          deliverReply(`⚠️ ${msg.message || 'Error occurred during streaming.'}`, []);
          this.accumulatedText = '';
          this.lastIndex = -1;
          break;
      }
    }

    send(messageText, language = 'en') {
      this.accumulatedText = '';
      this.lastIndex = -1;

      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(JSON.stringify({
            type: 'chat',
            message: messageText,
            language
          }));
          return true;
        } catch (e) {
          console.warn('[OpenClaw WS] Error sending WS message:', e.message);
        }
      }
      return false;
    }

    reconnectIfInactive() {
      if (!this.ws || this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
        this.connect(this.lastIndex >= 0 ? this.lastIndex : -1);
      }
    }
  }

  // Initialize Persistent Stream Manager
  activeStreamManager = new ChatStreamManager(sessionId);

  /**
   * Checks pending session status from server cache immediately
   */
  const checkPendingSessionStatus = async () => {
    if (activeStreamManager) activeStreamManager.reconnectIfInactive();
    try {
      const statusRes = await fetch(`/api/openclaw/chat-status?sessionId=${encodeURIComponent(sessionId)}&_t=${Date.now()}`, {
        cache: 'no-store'
      });
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        if (statusData.status === 'completed' && statusData.reply && !statusData.reply.includes('⏳')) {
          deliverReply(statusData.reply, statusData.sources);
          return true;
        }
      }
    } catch (e) {}
    return false;
  };

  // If waiting for reply from prior state, trigger status check immediately
  if (isWaitingForReply) {
    checkPendingSessionStatus();
  }

  // Immediate Tab Visibility / Focus Recovery
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      if (activeStreamManager) activeStreamManager.reconnectIfInactive();
      if (isWaitingForReply) checkPendingSessionStatus();
    }
  });

  window.addEventListener('focus', () => {
    if (activeStreamManager) activeStreamManager.reconnectIfInactive();
    if (isWaitingForReply) checkPendingSessionStatus();
  });

  openBtn.addEventListener('click', () => {
    const isHidden = modal.classList.contains('hidden');
    if (isHidden) {
      modal.classList.remove('hidden');
      hideBubble();
      sessionStorage.setItem('sed_openclaw_is_open', '1');
      if (activeStreamManager) activeStreamManager.reconnectIfInactive();
      checkPendingSessionStatus();
    } else {
      modal.classList.add('hidden');
      sessionStorage.setItem('sed_openclaw_is_open', '0');
    }
  });

  closeBtn.addEventListener('click', () => {
    modal.classList.add('hidden');
    sessionStorage.setItem('sed_openclaw_is_open', '0');
  });

  bubble.addEventListener('click', (e) => {
    if (e.target.closest('#sed-chat-bubble-close-btn')) {
      e.stopPropagation();
      hideBubble();
      bubbleDismissed = true;
      sessionStorage.setItem('sed_chat_bubble_dismissed', '1');
      return;
    }
    modal.classList.remove('hidden');
    hideBubble();
    sessionStorage.setItem('sed_openclaw_is_open', '1');
    if (activeStreamManager) activeStreamManager.reconnectIfInactive();
    checkPendingSessionStatus();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const query = input.value.trim();
    if (!query) return;

    input.value = '';

    // Append to stored messages and render
    const msgs = loadStoredMessages();
    msgs.push({ role: 'user', text: query, time: Date.now() });
    msgs.push({ role: 'bot', text: '', isThinking: true, time: Date.now() });
    saveStoredMessages(msgs);
    renderAllMessages();

    isWaitingForReply = true;
    const currentLang = getCurrentLanguage ? getCurrentLanguage() : lang;

    // 1. Dispatch over WebSocket for instant streaming
    if (activeStreamManager) {
      activeStreamManager.send(query, currentLang);
    }

    // 2. Parallel HTTP POST fallback with AbortController
    const abortCtrl = new AbortController();
    currentActiveAbortCtrl = abortCtrl;
    const postTimeoutTimer = setTimeout(() => {
      try { abortCtrl.abort(); } catch (err) {}
    }, 45000);

    // 3. Parallel fast status polling loop as secondary safeguard
    let pollActive = true;
    const runParallelStatusPoll = async () => {
      const startTime = Date.now();
      const maxPollMs = 120000;

      await new Promise(r => setTimeout(r, 2000));

      while (pollActive && isWaitingForReply && (Date.now() - startTime < maxPollMs)) {
        try {
          const res = await fetch(`/api/openclaw/chat-status?sessionId=${encodeURIComponent(sessionId)}&_t=${Date.now()}`, {
            cache: 'no-store'
          });
          if (res.ok) {
            const data = await res.json();
            if (data.status === 'completed' && data.reply && !data.reply.includes('⏳')) {
              deliverReply(data.reply, data.sources);
              pollActive = false;
              break;
            }
          }
        } catch (pollErr) {}

        await new Promise(r => setTimeout(r, 1500));
      }
    };

    runParallelStatusPoll();

    try {
      const response = await fetch('/api/openclaw/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId,
          message: query,
          language: currentLang,
          timestamp: new Date().toISOString()
        }),
        signal: abortCtrl.signal,
        cache: 'no-store'
      });

      clearTimeout(postTimeoutTimer);

      if (response.ok) {
        const data = await response.json();
        if (data.confidence !== 'pending' && data.reply && !data.reply.includes('⏳')) {
          pollActive = false;
          deliverReply(data.reply, data.sources);
        }
      }
    } catch (err) {
      clearTimeout(postTimeoutTimer);
    }

    setTimeout(() => {
      if (isWaitingForReply) {
        pollActive = false;
        deliverReply(c.agentProcessing, []);
      }
    }, 120000);
  });

  return widgetRoot;
}

/**
 * Updates UI labels on language change without clearing chat history
 */
function updateWidgetLanguage() {
  const lang = getLocale() || 'en';
  const c = chatTranslations[lang] || chatTranslations.en;

  const titleEl = document.getElementById('sed-chat-header-title');
  if (titleEl) titleEl.innerText = c.title;

  const subEl = document.getElementById('sed-chat-header-sub');
  if (subEl) subEl.innerText = c.subtitle;

  const inputEl = document.getElementById('sed-chat-input-field');
  if (inputEl) inputEl.setAttribute('placeholder', c.placeholder);

  const bubbleTitle = document.getElementById('sed-chat-bubble-title');
  if (bubbleTitle) bubbleTitle.innerText = c.bubbleHeader;

  const bubbleSpan = document.getElementById('sed-chat-bubble-span');
  if (bubbleSpan) bubbleSpan.innerText = c.bubblePrompt;

  const welcomeEl = document.getElementById('sed-welcome-msg');
  if (welcomeEl) welcomeEl.innerHTML = c.welcome;
}
